import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { useCRMHost } from '../CRMHostContext';
import {
  newPrivilegeCode, PRIVILEGE_UNSUPPORTED_FIELDS, tierIdFromName, toCrmPrivilegePatch, toUiPrivilege,
} from '../adapters';
import type { Tier as CrmTier } from '../crmClient';
import { PrivilegeScreen } from '../../screens/PrivilegeScreen';
import type { Privilege, TierId } from '../../screens/loyaltyData';

/**
 * ต่อหน้าสิทธิพิเศษแบบกดรับเข้ากับ CRM module (FR-ADM-19)
 *
 * หนึ่งสิทธิ์ = benefit code หนึ่งตัว พร้อมเงื่อนไขที่ผูกกับระดับสมาชิก
 * ลูกค้ากดรับแล้วได้ "รหัส" ที่มีอายุเป็นนาที ไม่ใช่คูปอง
 *
 * ⚠️ **เงื่อนไขเป็นชุดเดียวทุกระดับที่เลือก** — backend เขียนค่าเดียวกันลงทุกความสัมพันธ์
 *    ตั้งอายุรหัสหรือเพดานต่างกันรายระดับจากหน้านี้ไม่ได้
 * ⚠️ หน้าระดับสมาชิกแก้เพดานต่อคนของสิทธิ์เดียวกันได้ด้วย — แก้คนละที่จะทับกัน
 * ⚠️ ระดับสมาชิกของหน้านี้อ้างด้วยชื่อ (silver / gold / platinum) เหมือนหน้าระดับสมาชิก
 *    division ที่ตั้งชื่อระดับแบบอื่นจะจับคู่ไม่ได้ จึงเปิดให้ดูอย่างเดียว
 */
export function PrivilegeContainer() {
  const { api, dataMode } = useCRMHost();
  const isMock = dataMode === 'mock';

  const [privileges, setPrivileges] = useState<Privilege[] | undefined>(undefined);
  const [tiers, setTiers] = useState<CrmTier[] | undefined>(undefined);
  const [memberCounts, setMemberCounts] = useState<Map<string, number>>(new Map());
  const [state, setState] = useState<'loading' | 'ready' | 'error'>('loading');

  /** id ของระดับ → คีย์ที่หน้าจอใช้ และทางกลับ */
  const tierKeyById = useMemo(
    () => new Map((tiers ?? []).map((t) => [t.id, tierIdFromName(t.name)] as const)),
    [tiers],
  );
  const policyIdByKey = useMemo(() => {
    const out = new Map<TierId, number>();
    for (const t of tiers ?? []) {
      const key = tierIdFromName(t.name);
      /* ชื่อซ้ำคีย์กัน = จับคู่ไม่ได้แน่นอน — ตัวแรกชนะ แล้วหน้าถูกล็อกให้ดูอย่างเดียวอยู่ดี */
      if (!out.has(key)) out.set(key, t.id);
    }
    return out;
  }, [tiers]);

  /** ชื่อระดับซ้ำคีย์กัน = บันทึกแล้วกลุ่มเป้าหมายจะเพี้ยน จึงไม่เปิดให้บันทึก */
  const ambiguousTiers = useMemo(() => {
    const keys = (tiers ?? []).map((t) => tierIdFromName(t.name));
    return new Set(keys).size !== keys.length;
  }, [tiers]);

  const load = useCallback(() => {
    if (isMock) { setState('ready'); return; }
    let cancelled = false;
    setState('loading');

    Promise.all([
      api.listPrivileges(),
      api.listTiers(),
      /* จำนวนสมาชิกต่อระดับเป็นของเสริมของช่องกลุ่มเป้าหมาย ล้มแล้วหน้ายังใช้ได้ */
      api.listTierSummary().catch(() => null),
    ])
      .then(([page, tierPage, summary]) => {
        if (cancelled) return;
        const byId = new Map(tierPage.items.map((t) => [t.id, tierIdFromName(t.name)] as const));
        setTiers(tierPage.items);
        setPrivileges(page.items.map((b) => toUiPrivilege(b, byId)));
        setMemberCounts(
          summary
            ? new Map(summary.map((sum) => [tierIdFromName(sum.tier.name), sum.totalAccounts]))
            : new Map(),
        );
        setState('ready');
      })
      .catch(() => { if (!cancelled) setState('error'); });

    return () => { cancelled = true; };
  }, [isMock, api]);

  useEffect(() => load(), [load]);

  const tierOptions = useMemo(
    () => (tiers ?? []).map((t) => {
      const id = tierIdFromName(t.name);
      return { id, name: t.name, memberCount: memberCounts.get(id) ?? 0 };
    }),
    [tiers, memberCounts],
  );

  /**
   * บันทึกสิทธิ์หนึ่งรายการ
   *
   * id ของรายการใหม่ที่หน้าจอสร้างเป็น `pv-<timestamp>` ซึ่งไม่ใช่เลข
   * ใช้ตรงนี้แยกว่าเป็นการสร้างใหม่หรือแก้ของเดิม
   */
  const onSave = useCallback(async (p: Privilege) => {
    const patch = toCrmPrivilegePatch(p, policyIdByKey);
    const id = Number(p.id);
    const isNew = !(Number.isInteger(id) && id > 0);

    /* รูปเดิมถูกเอาออก — ล้างด้วย PATCH ปกติ ดูจากรายการที่โหลดมา ไม่ใช่จากค่าที่หน้าจอส่งมา */
    const had = privileges?.find((x) => x.id === p.id)?.imageUrl;
    if (!isNew && had && !p.imageUrl) patch.image = null;

    const saved = isNew
      /* 🔧 ส่ง code เองเพื่อเลี่ยงบั๊กตอนสร้างของระบบหลังบ้าน — ดู newPrivilegeCode */
      ? await api.createPrivilege({ ...patch, code: newPrivilegeCode(p.benefitKind ?? 'percent') })
      : await api.updatePrivilege(id, patch);

    /* อัปโหลดรูปหลังบันทึกสำเร็จ เพราะของใหม่เพิ่งมี id ตอนนี้ */
    if (p.imageFile) {
      try {
        await api.uploadPrivilegeImage(saved.id, p.imageFile);
      } catch (err) {
        const fresh = await api.listPrivileges();
        setPrivileges(fresh.items.map((b) => toUiPrivilege(b, tierKeyById)));
        throw new Error(
          err instanceof Error
            ? `บันทึกสิทธิ์แล้ว แต่อัปโหลดรูปไม่สำเร็จ — ${err.message}`
            : 'บันทึกสิทธิ์แล้ว แต่อัปโหลดรูปไม่สำเร็จ',
        );
      }
    }

    const fresh = await api.listPrivileges();
    setPrivileges(fresh.items.map((b) => toUiPrivilege(b, tierKeyById)));
  }, [api, policyIdByKey, tierKeyById, privileges]);

  if (isMock) return <PrivilegeScreen initialState="ready" />;

  return (
    <PrivilegeScreen
      initialState={state}
      privileges={privileges ?? []}
      tiers={tierOptions}
      /* ชื่อระดับจับคู่ไม่ได้ = ดูได้ แต่บันทึกไม่ได้ ดีกว่าบันทึกแล้วกลุ่มเป้าหมายเพี้ยน */
      onSave={ambiguousTiers ? undefined : onSave}
      onRefresh={load}
      refreshing={state === 'loading'}
      unsupportedFields={PRIVILEGE_UNSUPPORTED_FIELDS}
    />
  );
}
